import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Download, LogOut, Building2, Mail, Phone, MessageSquare, Trash2,
} from "lucide-react";
import { InfraAIWordmark } from "@/components/InfraAIWordmark";
import { SITE_LOGO_URL } from "@/components/SiteBrand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const STATUSES = ["new", "contacted", "archived"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");
  const [selected, setSelected] = React.useState(null);

  const email = sessionStorage.getItem("infraai_admin_email");

  const handleUnauthorized = React.useCallback(() => {
    sessionStorage.removeItem("infraai_logged_in");
    sessionStorage.removeItem("infraai_admin_email");
    nav("/admin/login");
  }, [nav]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ax.get(`${API}/admin/requests`);
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error("Failed to load requests");
      }
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  React.useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await ax.patch(`${API}/admin/requests/${id}`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? data : x)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked as ${status}`);
    } catch (err) {
      if (err?.response?.status === 401) handleUnauthorized();
      else toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await ax.delete(`${API}/admin/requests/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch (err) {
      if (err?.response?.status === 401) handleUnauthorized();
      else toast.error("Delete failed");
    }
  };

  const logout = async () => {
    try {
      await ax.post(`${API}/admin/logout`);
    } catch {
      // Ignore errors — clear session regardless
    }
    sessionStorage.removeItem("infraai_logged_in");
    sessionStorage.removeItem("infraai_admin_email");
    nav("/admin/login");
  };

  const exportCsv = () => {
    const cols = ["created_at", "name", "company", "email", "phone", "intent", "status", "message"];
    const rows = [cols.join(",")].concat(
      filtered.map((r) =>
        cols
          .map((c) => `"${String(r[c] ?? "").replace(/"/g, '""').replace(/\n/g, " ")}"`)
          .join(",")
      )
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `infraai-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = items.filter((x) => x.status === s).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-bg" data-testid="admin-dashboard">
      <header className="border-b border-line bg-bg-paper/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={SITE_LOGO_URL}
              alt=""
              className="h-8 w-8 rounded"
            />
            <div>
              <div className="font-heading text-base tracking-tight">
                <InfraAIWordmark variant="sm" /> · Admin
              </div>
              <div className="font-mono text-[10px] text-ink-dim tracking-widest2">
                {email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="text-ink-mute hover:text-ink p-2"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export"
              className="hidden sm:inline-flex items-center gap-2 border border-line text-ink-mute hover:text-ink hover:border-amber transition-colors font-mono text-[11px] tracking-widest2 uppercase px-3 py-2"
            >
              <Download size={13} /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-line text-ink-mute hover:text-ink hover:border-cyan transition-colors font-mono text-[11px] tracking-widest2 uppercase px-3 py-2"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-8">
        {/* metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-line/40 border border-line/60 mb-8">
          <Stat label="Total" value={items.length} accent="cyan" />
          <Stat label="New" value={counts.new || 0} accent="amber" />
          <Stat label="Contacted" value={counts.contacted || 0} accent="cyan" />
          <Stat label="Archived" value={counts.archived || 0} accent="dim" />
        </div>

        {/* filter tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`font-mono text-[11px] tracking-widest2 uppercase px-3 py-1.5 border transition-colors ${
                filter === s
                  ? "border-cyan text-cyan bg-cyan/5"
                  : "border-line text-ink-mute hover:text-ink"
              }`}
            >
              {s} {s !== "all" && `· ${counts[s] || 0}`}
            </button>
          ))}
        </div>

        {/* table */}
        <div className="border border-line">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-line bg-bg-paper/40 font-mono text-[10px] tracking-widest2 text-ink-dim uppercase">
            <div className="col-span-3">Name · Company</div>
            <div className="col-span-3 hidden md:block">Email</div>
            <div className="col-span-2 hidden md:block">Intent</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {loading && (
            <div className="p-10 text-center text-ink-dim font-mono text-sm" data-testid="admin-loading">
              Loading…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-ink-dim font-mono text-sm" data-testid="admin-empty">
              No requests yet.
            </div>
          )}
          {!loading && filtered.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelected(r)}
              data-testid={`admin-row-${r.id}`}
              className="w-full grid grid-cols-12 gap-4 px-5 py-4 border-b border-line/60 last:border-b-0 hover:bg-bg-paper/60 transition-colors text-left"
            >
              <div className="col-span-3">
                <div className="text-ink text-[14px]">{r.name}</div>
                <div className="text-ink-dim text-[12px] font-mono">{r.company}</div>
              </div>
              <div className="col-span-3 hidden md:block text-ink-mute text-[13px] font-mono truncate">
                {r.email}
              </div>
              <div className="col-span-2 hidden md:block text-ink-mute text-[12px] font-mono">
                {r.intent}
              </div>
              <div className="col-span-2 text-ink-dim text-[12px] font-mono">
                {new Date(r.created_at).toLocaleString()}
              </div>
              <div className="col-span-2 text-right">
                <StatusPill status={r.status} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="admin-drawer"
        >
          <div
            className="w-full max-w-lg h-full bg-bg-paper border-l border-line overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-line flex items-center justify-between">
              <div>
                <div className="font-heading text-xl tracking-tight">{selected.name}</div>
                <div className="text-ink-dim font-mono text-[12px]">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-ink-mute hover:text-ink"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-5">
              <DetailRow icon={Building2} label="Company" value={selected.company} />
              <DetailRow icon={Mail} label="Email" value={<a className="text-cyan hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a>} />
              <DetailRow icon={Phone} label="Phone" value={selected.phone || "—"} />
              <DetailRow label="Intent" value={selected.intent} />
              <DetailRow icon={MessageSquare} label="Message" value={selected.message || "—"} multiline />

              <div>
                <div className="font-mono text-[10px] tracking-widest2 text-ink-dim uppercase mb-2">
                  Status
                </div>
                <div className="flex gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`status-${s}`}
                      className={`font-mono text-[11px] tracking-widest2 uppercase px-3 py-2 border transition-colors ${
                        selected.status === s
                          ? "border-cyan text-cyan bg-cyan/5"
                          : "border-line text-ink-mute hover:text-ink"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-line">
                <button
                  onClick={() => remove(selected.id)}
                  data-testid="admin-delete"
                  className="inline-flex items-center gap-2 border border-line text-ink-mute hover:text-red-400 hover:border-red-400/60 transition-colors font-mono text-[11px] tracking-widest2 uppercase px-3 py-2"
                >
                  <Trash2 size={13} /> Delete request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colors = {
    cyan: "text-cyan",
    amber: "text-amber",
    dim: "text-ink-mute",
  };
  return (
    <div className="bg-bg p-6">
      <div className={`font-heading text-4xl tracking-tighter ${colors[accent]}`}>
        {value}
      </div>
      <div className="font-mono text-[10px] tracking-widest2 text-ink-dim uppercase mt-2">
        {label}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "border-amber/50 text-amber bg-amber/5",
    contacted: "border-cyan/50 text-cyan bg-cyan/5",
    archived: "border-line text-ink-dim",
  };
  return (
    <span className={`font-mono text-[10px] tracking-widest2 uppercase px-2.5 py-1 border ${map[status]}`}>
      {status}
    </span>
  );
}

function DetailRow({ icon: Icon, label, value, multiline }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon size={14} className="text-ink-dim mt-1" />}
      <div className="flex-1">
        <div className="font-mono text-[10px] tracking-widest2 text-ink-dim uppercase mb-1">
          {label}
        </div>
        <div className={`text-ink text-[14px] ${multiline ? "whitespace-pre-wrap leading-relaxed" : ""}`}>
          {value}
        </div>
      </div>
    </div>
  );
}
